import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupsIcon from "@mui/icons-material/Groups";
import PercentIcon from "@mui/icons-material/Percent";
import StorefrontIcon from "@mui/icons-material/Storefront";
import StoreIcon from "@mui/icons-material/Store";
import LocationOnIcon from "@mui/icons-material/LocationOn";

function Settings() {
  const cards = [
    {
      title: "Profile Settings",
      description: "Update your account and password.",
      to: "/user/profile-settings",
      icon: <AccountCircleIcon sx={{ fontSize: 28, color: "#667eea" }} />,
    },
    {
      title: "Shop Profile",
      description: "Manage shop details, logo, and Google Calendar sync.",
      to: "/user/shop-profile",
      icon: <StoreIcon sx={{ fontSize: 28, color: "#2563eb" }} />,
    },
    {
      title: "Tax Settings",
      description: "Set the default tax percentage for taxable customers.",
      to: "/user/tax-settings",
      icon: <PercentIcon sx={{ fontSize: 28, color: "#ef4444" }} />,
    },
    {
      title: "Roles and Permissions",
      description: "Manage access, roles, and permission settings.",
      to: "/user/roles",
      icon: (
        <AdminPanelSettingsIcon sx={{ fontSize: 28, color: "#f59e0b" }} />
      ),
    },
    {
      title: "Team Management",
      description: "Manage team members and employee access.",
      to: "/user/employee",
      icon: <GroupsIcon sx={{ fontSize: 28, color: "#10b981" }} />,
    },
    {
      title: "Vendors",
      description: "Review existing vendors and manage vendor records.",
      to: "/user/vendors",
      icon: <StorefrontIcon sx={{ fontSize: 28, color: "#0f766e" }} />,
    },
    {
      title: "Location",
      description: "Add, edit, and delete storage locations for your tires.",
      to: "/user/locations",
      icon: <LocationOnIcon sx={{ fontSize: 28, color: "#7c3aed" }} />,
    },
  ];

  const settingSections =
    cards.length >= 9
      ? cards
      : [
          ...cards,
          ...Array.from({ length: 9 - cards.length }, (_, index) => ({
            title: `empty-${index}`,
            empty: true,
          })),
        ];

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Settings
            </Typography>
          </Breadcrumbs>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ zIndex: 1, position: "relative" }}>
            <SettingsIcon sx={{ fontSize: 38, color: "white" }} />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
              >
                Manage system configuration and account setup.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 3,
          }}
        >
          {settingSections.map((card) =>
            card.empty ? (
              <Box
                key={card.title}
                aria-hidden="true"
                sx={{
                  minHeight: 180,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "rgba(148, 163, 184, 0.35)",
                  backgroundColor: "rgba(255,255,255,0.45)",
                }}
              />
            ) : (
              <Box
                key={card.title}
                component={Link}
                to={card.to}
                sx={{
                  textDecoration: "none",
                  minHeight: 180,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>{card.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  {card.description}
                </Typography>
              </Box>
            ),
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Settings;
